import { AvatarText } from "@amzn/stencil-react-components/avatar";
import { Card } from "@amzn/stencil-react-components/card";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { useController, useFormContext } from "react-hook-form";
import { RoundedAvatar } from "@amzn/it-shop-shared-components";
import { FormField } from "@/common/components/FormField/form-field";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { CheckoutFormState } from "@/pages/Checkout/checkout-form.types";
import { REQUIRED_FIELD_ERROR, validateCostCenter, validateRequiredString } from "@/pages/Checkout/checkout-form.util";
import { useGetManagerApprovalCard } from "@/pages/Checkout/components/GetManagerApprovalCard/hooks/use-get-manager-approval-card.hook";
import React from "react";

const STRING_PATH = "checkout.formPanel.managerCard";

export const GetManagerApprovalCard: React.FC = () => {
  const { control } = useFormContext<CheckoutFormState>();
  const { isDeviceReturnRequired, manager, managerFullName, managerSubtitle } =
    useGetManagerApprovalCard();
  const { field: businessJustificationField, fieldState: businessJustificationState } =
    useController<CheckoutFormState, "businessJustification">({
      control,
      name: "businessJustification",
      rules: { validate: validateRequiredString },
    });
  const { field: serialNumberField, fieldState: serialNumberState } = useController<
    CheckoutFormState,
    "serialNumberForReturnDevice"
  >({
    control,
    name: "serialNumberForReturnDevice",
    rules: {
      validate: (value) => !isDeviceReturnRequired || validateRequiredString(value),
    },
  });
  const { field: costCenterField, fieldState: costCenterState } = useController<
    CheckoutFormState,
    "costCenter"
  >({
    control,
    name: "costCenter",
    rules: { validate: validateCostCenter },
  });
  const { field: carCodeField, fieldState: carCodeState } = useController<
    CheckoutFormState,
    "carCode"
  >({
    control,
    name: "carCode",
  });
  const { field: managerConfirmationField, fieldState: managerConfirmationState } = useController<
    CheckoutFormState,
    "managerConfirmationCheckbox"
  >({
    control,
    name: "managerConfirmationCheckbox",
    rules: { required: REQUIRED_FIELD_ERROR },
  });

  return (
    <Card data-testid="manager-approval-card" padding="S300" width="100%">
      <Col gridGap="S300" width="100%">
        <Col gridGap="S300">
          <Text fontSize="T400" fontWeight="bold" color="neutral90">
            {getString(`${STRING_PATH}.step1`)}
          </Text>
          <Text fontSize="T200">{getString(`${STRING_PATH}.description`)}</Text>
        </Col>
        <Col gridGap="S300">
          <Text fontSize="T300" fontWeight="bold" color="neutral90">
            {getString(`${STRING_PATH}.yourManager`)}
          </Text>
          {manager?.basicInfo && (
            <RoundedAvatar fullName={managerFullName} username={manager.username ?? ""}>
              {manager.jobInfo && <AvatarText>{managerSubtitle}</AvatarText>}
            </RoundedAvatar>
          )}
        </Col>
        <Col gridGap="S300" width="85%">
          <FormField
            label={getString(`${STRING_PATH}.businessJustification`)}
            value={businessJustificationField.value}
            onChange={businessJustificationField.onChange}
            onBlur={businessJustificationField.onBlur}
            inputRef={businessJustificationField.ref}
            error={businessJustificationState.error?.message}
            placeholder={getString(`${STRING_PATH}.placeholderBJ`)}
            testId="business-justification-input"
            isTextArea
          />
          {isDeviceReturnRequired && (
            <FormField
              label={getString(`${STRING_PATH}.serialNumberForReturnDevice`)}
              value={serialNumberField.value}
              onChange={serialNumberField.onChange}
              onBlur={serialNumberField.onBlur}
              inputRef={serialNumberField.ref}
              error={serialNumberState.error?.message}
              placeholder={getString(`${STRING_PATH}.placeHolderSerialNumbersForReturnDevices`)}
              tooltipText={getString(`${STRING_PATH}.tooltipSerialNumbersForReturnDevices`)}
              testId="return-devices-input"
              isTextArea
            />
          )}
          <FormField
            label={getString(`${STRING_PATH}.costCenter`)}
            value={costCenterField.value}
            onChange={costCenterField.onChange}
            onBlur={costCenterField.onBlur}
            inputRef={costCenterField.ref}
            error={costCenterState.error?.message}
            placeholder={getString(`${STRING_PATH}.placeholderCostCenter`)}
            chipText={getString(`${STRING_PATH}.askYourManagerCostCenter`)}
            testId="cost-center-input"
          />
          <FormField
            label={getString(`${STRING_PATH}.carCode`)}
            value={carCodeField.value}
            onChange={carCodeField.onChange}
            onBlur={carCodeField.onBlur}
            inputRef={carCodeField.ref}
            error={carCodeState.error?.message}
            placeholder={getString(`${STRING_PATH}.placeholderCarCode`)}
            chipText={getString(`${STRING_PATH}.askYourManagerCarCode`)}
            testId="car-code-input"
          />
        </Col>
        <Row gridGap="S200">
          <Checkbox
            data-testid="manager-confirmation-checkbox"
            checked={managerConfirmationField.value}
            error={managerConfirmationState.invalid}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              managerConfirmationField.onChange(event.target.checked)
            }
          />
          <Label color="neutral90">{getString(`${STRING_PATH}.managerConfirmation`)}</Label>
        </Row>
      </Col>
    </Card>
  );
};
